import type {ReactNode, SVGProps} from "react";

// Inline stroke icons (24x24, currentColor) — emoji render differently on every OS.

function Svg({size = 18, children, ...rest}: SVGProps<SVGSVGElement> & { size?: number; children: ReactNode }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            {...rest}
        >
            {children}
        </svg>
    );
}

type IconProps = { size?: number };

export const ChatIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        <path d="M8 10h8M8 14h5" />
    </Svg>
);

export const SpeechIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.5 9.5 0 0 1-3.8-.8L3 21l1.8-4.6A8 8 0 0 1 3 11.5 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />
    </Svg>
);

export const HelpIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
    </Svg>
);

export const HeadsetIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </Svg>
);

export const MailIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
    </Svg>
);

export const CloseIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <path d="M18 6 6 18M6 6l12 12" />
    </Svg>
);

export const EndChatIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        <line x1="4" y1="4" x2="20" y2="20" />
    </Svg>
);

export const PaperclipIcon = ({size}: IconProps) => (
    <Svg size={size}>
        <path d="m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" />
    </Svg>
);

// Launcher setting values that get a real icon; anything else (👋, ⚡, custom) is shown as text.
const LAUNCHER_ICONS: Record<string, (p: IconProps) => ReactNode> = {
    "💬": ChatIcon,
    "🗨️": SpeechIcon,
    "🗨": SpeechIcon,
    "❓": HelpIcon,
    "🎧": HeadsetIcon,
};

export function LauncherIcon({value, size}: { value?: string; size?: number }) {
    const v = (value || "💬").trim();
    const Icon = LAUNCHER_ICONS[v];
    return Icon ? <Icon size={size} /> : <>{v}</>;
}
